import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const DEFAULT_WIDTH = 350;
const MIN_WIDTH = 300;
const MAX_WIDTH = 450;

const Wrapper = styled.div<{ $width: number }>`
  position: relative;
  flex: 0 0 ${({ $width }) => $width}px;
  width: ${({ $width }) => $width}px;
  height: 100%;
  overflow: hidden;
  overflow-y: auto;
  background: var(--white-100);
  border-right: 1px solid var(--field-border-default);
`;

const DragHandle = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 4px;
  cursor: col-resize;
  z-index: 1;
  background: transparent;
  transition: background 150ms ease;

  &:hover,
  &[data-dragging='true'] {
    background: var(--blue-40, #6b8cff);
  }
`;

interface Props {
  children: React.ReactNode;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  className?: string;
}

export const ResizableSidebar: React.FC<Props> = ({
  children,
  defaultWidth = DEFAULT_WIDTH,
  minWidth = MIN_WIDTH,
  maxWidth = MAX_WIDTH,
  className,
}) => {
  const [width, setWidth] = useState(defaultWidth);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const handleRef = useRef<HTMLDivElement>(null);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging.current) {
        return;
      }
      const delta = e.clientX - startX.current;
      const next = Math.min(
        maxWidth,
        Math.max(minWidth, startWidth.current + delta),
      );

      setWidth(next);
    },
    [minWidth, maxWidth],
  );

  const onMouseUp = useCallback(() => {
    if (!isDragging.current) {
      return;
    }
    isDragging.current = false;
    if (handleRef.current) {
      handleRef.current.removeAttribute('data-dragging');
    }
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [onMouseMove]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      startX.current = e.clientX;
      startWidth.current = width;
      if (handleRef.current) {
        handleRef.current.setAttribute('data-dragging', 'true');
      }
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [width, onMouseMove, onMouseUp],
  );

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [onMouseMove, onMouseUp]);

  return (
    <Wrapper $width={width} className={className}>
      {children}
      <DragHandle ref={handleRef} onMouseDown={onMouseDown} />
    </Wrapper>
  );
};
